import React, { useCallback } from 'react';
import styled from 'styled-components';
import CustomTabs from '@components/dataDisplay/tab/CustomTabs';
import { allNotiRead } from '@store/firebase/notification/add-notification';
import { useRecoilValue } from 'recoil';
import { currentUserIDState } from '@atom/setting/user';
import { removeAllNoti } from '@store/firebase/notification/remove-notification';
import NotiList from './NotiList';
import { CollectionPath } from '@typings/collections.enum';
import { useCollection } from '@nandorojo/swr-firestore';
import { Inotification } from '@typings/notification/notification.interface';
import { NOTI_TYPE } from '@typings/notification/notificationType';
import { fuego } from '@store/firebase';

const TextBtn = styled.button`
  color: var(--primaryColor);
  font-size: 12px;
  padding: 8px;
  border-radius: var(--cardBorderRadius);
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--inkColor);
  }
`;

const ControlSecContainer = styled.div`
  height: 62px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

function ControlSec() {
  const currentUserUid = useRecoilValue(currentUserIDState);

  const handleAllRead = useCallback(async () => {
    await allNotiRead({ userId: currentUserUid });
  }, [currentUserUid]);

  const handleAllDelete = useCallback(async () => {
    await removeAllNoti({ userId: currentUserUid });
  }, [currentUserUid]);

  return (
    <ControlSecContainer>
      <TextBtn
        onClick={handleAllDelete}
        style={{ color: 'var(--subTextColor)' }}
      >
        <strong>모두 삭제</strong>
      </TextBtn>
      <TextBtn onClick={handleAllRead}>
        <strong>모두 읽음</strong>
      </TextBtn>
    </ControlSecContainer>
  );
}

/**
 * 알림 목록 UI 전용 컴포넌트.
 * 브라우저 알림 로직은 NotificationBrowserAlert에서 상시 처리한다.
 */
function Notifications() {
  const limit = 10;
  const currentUserId = fuego.auth().currentUser?.uid;
  const collectionPath = `${CollectionPath.USERS}/${currentUserId}/${CollectionPath.NOTIFICATIONS}`;

  const { data: allList, mutate: allMutate } = useCollection<Inotification>(
    collectionPath,
    {
      orderBy: ['createdAt', 'desc'],
      limit,
      listen: true,
      ignoreFirestoreDocumentSnapshotField: false,
    },
    {
      revalidateOnFocus: false,
      refreshWhenHidden: false,
      refreshWhenOffline: false,
      refreshInterval: 0,
    },
  );

  const { data: commentList, mutate: commentMutate } =
    useCollection<Inotification>(
      collectionPath,
      {
        orderBy: ['createdAt', 'desc'],
        where: ['type', '==', NOTI_TYPE.COMMENT],
        listen: true,
        limit,
        ignoreFirestoreDocumentSnapshotField: false,
      },
      {
        revalidateOnFocus: false,
        refreshWhenHidden: false,
        refreshWhenOffline: false,
        refreshInterval: 0,
      },
    );

  const { data: taggedList, mutate: taggedMutate } =
    useCollection<Inotification>(
      collectionPath,
      {
        orderBy: ['createdAt', 'desc'],
        where: ['type', '==', NOTI_TYPE.TAGGED],
        listen: true,
        limit,
        ignoreFirestoreDocumentSnapshotField: false,
      },
      {
        revalidateOnFocus: false,
        refreshWhenHidden: false,
        refreshWhenOffline: false,
        refreshInterval: 0,
      },
    );

  const tabList = [
    {
      id: '1',
      order: 0,
      value: '전체',
      contents: (
        <NotiList
          list={allList!}
          limit={limit}
          mutate={allMutate}
          collectionPath={collectionPath}
        />
      ),
    },
    {
      id: '2',
      order: 1,
      value: '댓글',
      contents: (
        <NotiList
          list={commentList!}
          limit={limit}
          mutate={commentMutate}
          collectionPath={collectionPath}
          where={['type', '==', NOTI_TYPE.COMMENT]}
        />
      ),
    },
    {
      id: '3',
      order: 2,
      value: '태그됨',
      contents: (
        <NotiList
          list={taggedList!}
          limit={limit}
          mutate={taggedMutate}
          collectionPath={collectionPath}
          where={['type', '==', NOTI_TYPE.TAGGED]}
        />
      ),
    },
  ];
  return (
    <>
      <CustomTabs
        tabList={tabList}
        position="flex-start"
        btnSideSec={<ControlSec />}
        customBtnId="noti"
        noUnderlineAni={true}
        handleOffset={false}
        tabBtnSecStyle={{ padding: '0 12px', height: '50px' }}
      />
    </>
  );
}
export default React.memo(Notifications);
